/** @jsx jsx */
import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ClipboardEvent,
	type ElementRef,
	type FocusEvent,
	type KeyboardEvent,
	type Ref,
	type RefObject,
} from 'react';
import { styled, jsx, css } from '@compiled/react';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import ArrowRightIcon from '@atlaskit/icon/core/migration/arrow-right';
import Lozenge from '@atlaskit/lozenge';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { Pressable, xcss } from '@atlaskit/primitives';
import type { GroupBase } from '@atlaskit/react-select/types';
import { components, type OptionProps, type MenuListComponentProps } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { CellWrapper as CellWrapperNew } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import LazySelect from '@atlassian/jira-business-fields/src/common/ui/lazy-select/index.tsx';
import { getPopupComponentWithHeight } from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import {
	StatusCategoryIds,
	StatusCategoryTypes,
	statusCategoryForId,
} from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { UNSAFE_noExposureExp, expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import type {
	StatusTransition,
	StatusValue,
} from '@atlassian/jira-issue-field-status/src/common/types.tsx';
import { StatusService } from '@atlassian/jira-issue-field-status/src/services/status-service/index.tsx';
import { TransitionsQuery } from '@atlassian/jira-issue-field-status/src/services/transitions-query/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { OptionWithButtonStyle } from '@atlassian/jira-select-with-footer-options/src/OptionWithButtonStyle.tsx';
import { GroupComponentWithStickyFooter } from '@atlassian/jira-select-with-footer-options/src/GroupComponentWithStickyFooter.tsx';
import { STICKY_FOOTER_GROUP_TYPE } from '@atlassian/jira-select-with-footer-options/src/constant.tsx';
import { OptionWithAutoKeyboardScroll } from '@atlassian/jira-select-with-footer-options/src/OptionWithAutoKeyboardScroll.tsx';
import { InlineConfigButtons } from '@atlassian/jira-inline-config-buttons-for-select/src/index.tsx';
import { DEFAULT_ROW_HEIGHT } from '../../../common/constants.tsx';
import LazySelectFetchError from '../../../common/ui/lazy-select-fetch-error/index.tsx';
import {
	CellContentContainer,
	CellContentWrapper,
	CellWrapper,
} from '../../../common/ui/list-cell/styled.tsx';
import { useIsIssueSelected } from '../../../controllers/bulk-selection/bulk-selection-store/index.tsx';
import { CreateStatusButton } from './create-status-button/CreateStatusButton.tsx';
import messages from './messages.tsx';
import type {
	FormattedStatusOptionLabelProps,
	ServiceWrapperProps,
	StatusCellProps,
	StatusTransitionOption,
	TransitionsProps,
	TransitionsQueryChildrenProps,
	TransitionStatus,
} from './types.tsx';
import { toAppearance, transformValue } from './utils.tsx';
import { EditStatusModalEntrypointTrigger } from './configure-status/EditStatusModalEntrypointTrigger.tsx';
import { messages as editStatusMessages } from './configure-status/messages.tsx';

const serviceProjectType = 'business';
const CREATE_STATUS = 'createStatus';

type CreateStatusOption = {
	label: string;
	value: string;
	optionType: typeof CREATE_STATUS;
	groupType: string;
};

type SelectOption = StatusTransitionOption | CreateStatusOption;

type OptionWithInlineConfigButtonProps = {
	children: React.ReactNode;
	projectId: string;
	projectKey: string;
	issueTypeId: string;
	statusNames: string[];
	editLabelText: string;
	showEditButton: boolean;
	menuListRef: RefObject<HTMLDivElement>;
} & OptionProps<SelectOption>;

const Option = ({ children, ...props }: OptionProps<SelectOption>) => {
	if (props.data.optionType === CREATE_STATUS) {
		return (
			<OptionWithButtonStyle {...props} css={CreateStatusOptionStyle}>
				{children}
			</OptionWithButtonStyle>
		);
	}
	return <components.Option {...props}>{children}</components.Option>;
};

const OptionWithInlineConfigButton = ({
	children,
	projectId,
	issueTypeId,
	projectKey,
	statusNames,
	editLabelText,
	showEditButton,
	menuListRef,
	...props
}: OptionWithInlineConfigButtonProps) => {
	if (props.data.optionType === CREATE_STATUS) {
		return (
			<OptionWithButtonStyle {...props} css={CreateStatusOptionStyle}>
				{children}
			</OptionWithButtonStyle>
		);
	}
	const selectOption: StatusTransitionOption = props.data;
	return (
		<OptionWithAutoKeyboardScroll {...props} containerRef={menuListRef}>
			<InlineConfigButtons
				editActionSubjectId="editStatusButton"
				editLabelText={editLabelText}
				isFocused={props.isFocused}
				showEditButton={Boolean(selectOption.transition && showEditButton)}
				queryParams={{
					status: {
						id: selectOption.transition?.to?.id ?? '',
						statusName: selectOption.transition?.to?.name ?? '',
						statusCategory:
							statusCategoryForId(selectOption.transition?.to?.statusCategory?.id) ?? '',
					},
					projectId,
					issueTypeId,
					projectKey,
					statusNames,
				}}
				EditButtonWrapper={EditStatusModalEntrypointTrigger}
			>
				{children}
			</InlineConfigButtons>
		</OptionWithAutoKeyboardScroll>
	);
};

export const FormattedStatusOptionLabel = memo<FormattedStatusOptionLabelProps>(
	({ option, hasDuplicateStatusesInTargets }: FormattedStatusOptionLabelProps) => {
		if (option.transition == null) {
			return null;
		}
		return (
			<OptionWrapper>
				{hasDuplicateStatusesInTargets ? (
					<TransitionWrapper>
						<TransitionLabel>
							<TransitionName>{option.transition?.name}</TransitionName>
							<ArrowRightIcon LEGACY_size="small" label="" />
							<StatusName>
								<Lozenge
									isBold={isVisualRefreshEnabled()}
									appearance={toAppearance(option.transition.to)}
								>
									{option.transition.to.name}
								</Lozenge>
							</StatusName>
						</TransitionLabel>
					</TransitionWrapper>
				) : (
					<>
						<Lozenge
							isBold={isVisualRefreshEnabled()}
							appearance={toAppearance(option.transition.to)}
						>
							{option.transition.to.name}
						</Lozenge>
					</>
				)}
			</OptionWrapper>
		);
	},
);

const ServiceWrapper = memo<ServiceWrapperProps>(
	({
		id,
		name,
		categoryId,
		issueKey,
		children,
		onStatusChange = noop,
		issueId,
	}: ServiceWrapperProps) => {
		const { baseUrl } = useTenantContext();
		const initialValue = {
			id,
			name,
			statusCategory: { id: categoryId },
		};

		const statusServiceChildren = useCallback(
			({
				value: statusFromService,
				loading: saving,
				transitionStatus,
			}: {
				value: StatusValue;
				loading: boolean;
				transitionStatus: TransitionStatus;
			}) => (
				<TransitionsQuery
					issueKey={issueKey}
					baseUrl={baseUrl}
					status={statusFromService}
					isSaving={saving}
					projectType={serviceProjectType}
				>
					{({ data: transitions, loading, fetch: fetchTransitions }) =>
						children({
							transitions,
							statusFromService,
							transitionStatus,
							serviceBusy: saving || loading,
							fetchTransitions,
						})
					}
				</TransitionsQuery>
			),
			[issueKey, baseUrl, children],
		);

		return (
			<StatusService
				appearance="lozenge"
				projectType={serviceProjectType}
				initialValue={initialValue}
				issueKey={issueKey}
				issueId={issueId}
				baseUrl={baseUrl}
				onSubmit={noop}
				onSuccess={onStatusChange}
				onFailure={noop}
				onEditStart={noop}
				onEditCancel={noop}
			>
				{statusServiceChildren}
			</StatusService>
		);
	},
);
ServiceWrapper.displayName = 'ServiceWrapper';

const POPUP_OFFSET: [number, number] =
	// we should push it down by 2px to adjust the border withs and align with the cell
	[-gridSize, 2 - DEFAULT_ROW_HEIGHT];

const Transitions = memo(
	({
		transitions,
		statusFromService,
		transitionStatus,
		fetchTransitions,
		serviceBusy,
		isMenuOpen,
		isSelected,
		isHovering,
		isLozengeBold,
		width,
		height,
		wrapperRef,
		inputRef,
		onMenuOpen,
		onPopupClose,
		inputValue: inputValueNew,
		onInputChange = noop,
		issueTypeId,
	}: TransitionsProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { formatMessage } = useIntl();
		const [availableTransitions, setAvailableTransitions] = useState<StatusTransition[] | null>(
			transitions,
		);
		const [transitionsReady, setTransitionsReady] = useState(false);
		const [inputValueOld, setInputValueOld] = useState('');
		const inputValue = fg('jira_list_keyboard_nav') ? inputValueNew : inputValueOld;

		// The kill-switch for the experiment jira_inline_create_status_list_view using Statsig Dynamic Configs
		const project = expVal('jira_inline_create_status_list_view_kill_switch', 'value', false)
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useProject()
			: null;
		const isAdmin = project?.permissions?.administerProject ?? false;
		const isTMP = project?.isSimplified ?? false;
		const { id: projectId, key: projectKey, type: projectType } = project || { type: null };

		const [expConfig] = UNSAFE_noExposureExp('jira_inline_create_status_list_view');
		const shouldShowCreateStatusButton =
			isAdmin && isTMP && expConfig.get('isInlineCreateStatusEnabled', false);

		// transitions prop changes on every render, this is used as a perf improvement
		useEffect(() => {
			if (!isEqual(transitions, availableTransitions)) {
				setAvailableTransitions(transitions);
				setTimeout(() => {
					setTransitionsReady(true);
				}, 100);
			}
		}, [transitions, availableTransitions]);

		// select input loses focus when we load options and re-render popup
		useEffect(() => {
			if (serviceBusy === false && inputRef?.current) {
				setTimeout(() => {
					inputRef.current?.focus();
				}, 1);
			}
		}, [serviceBusy, inputRef]);

		useEffect(() => {
			if (isMenuOpen && (!transitions || !transitions.length) && fg('jira_list_keyboard_nav')) {
				fetchTransitions(createAnalyticsEvent({}));
			}
		}, [createAnalyticsEvent, fetchTransitions, isMenuOpen, transitions]);

		const handleMenuOpenChange = useCallback(() => {
			fetchTransitions(createAnalyticsEvent({}));
		}, [fetchTransitions, createAnalyticsEvent]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in useEffect above
		 */
		const handleContentKeyDown = useCallback(
			(e: KeyboardEvent<Element>) => {
				if (!fg('jira_list_keyboard_nav') && e.key === 'Escape') {
					e.stopPropagation();
					e.preventDefault();
					onMenuOpen(false);
					wrapperRef?.current?.focus();
				}
			},
			[wrapperRef, onMenuOpen],
		);

		const handleTransitionChange = useCallback(
			(option: SelectOption | null) => {
				const transitionOption: StatusTransitionOption | null = option;
				if (option?.optionType === CREATE_STATUS) {
					// This is to allow keyboard users trigger the CreateStatusModal
					createStatusButtonRef?.current?.click();
				} else if (transitionOption?.transition) {
					transitionStatus(transitionOption?.transition, createAnalyticsEvent({}));
				}
				onMenuOpen(false);
				wrapperRef?.current &&
					setTimeout(() => {
						wrapperRef?.current?.focus();
					});
			},
			[createAnalyticsEvent, onMenuOpen, wrapperRef, transitionStatus],
		);

		const handleInputChange = useCallback(
			(value: string) => {
				setInputValueOld(value);
			},
			[setInputValueOld],
		);

		const handleInputChangeNew = useCallback(
			(value: string) => {
				/**
				 * Wait for transitions to finish loading, otherwise it clears inputValue
				 */
				if (transitionsReady) {
					onInputChange(value);
				}
			},
			[onInputChange, transitionsReady],
		);

		const selectValue = useMemo(() => transformValue(statusFromService), [statusFromService]);

		// we need to sort the options so that the selected status is at the top of the list
		// as the first option is hovered by default and will be selected if `Enter` is pressed
		// immediately after the select dropdown is shown
		const selectOptions: StatusTransitionOption[] | GroupBase<SelectOption>[] = useMemo(() => {
			let selectedStatusOption: StatusTransitionOption | null = null;
			const statusOptions: StatusTransitionOption[] = [];
			(availableTransitions || []).forEach((transitionOption) => {
				if (transitionOption.name == null) {
					return;
				}
				const statusOption = {
					label: transitionOption.to.name,
					value: transitionOption.to.name,
					transition: transitionOption,
				};
				if (transitionOption.to.name === statusFromService?.name) {
					selectedStatusOption = statusOption;
				} else {
					statusOptions.push(statusOption);
				}
			});
			if (selectedStatusOption != null) {
				statusOptions.unshift(selectedStatusOption);
			}

			if (
				statusOptions.length &&
				isTMP &&
				isAdmin &&
				expVal('jira_inline_create_status_list_view', 'isInlineCreateStatusEnabled', false)
			) {
				const createStatusOption: CreateStatusOption[] = [
					{
						label: formatMessage(messages.createStatus),
						value: CREATE_STATUS,
						optionType: CREATE_STATUS,
						groupType: STICKY_FOOTER_GROUP_TYPE,
					},
				];
				return [
					{ options: statusOptions, label: undefined },
					{ options: createStatusOption, label: undefined },
				];
			}
			return statusOptions;
		}, [availableTransitions, isTMP, isAdmin, statusFromService?.name, formatMessage]);

		const hasDuplicateStatusesInTargets = useMemo(() => {
			const targetStatusNames = availableTransitions
				? availableTransitions.map((transition) => transition.to.name)
				: [];
			return targetStatusNames.some(
				(statusName, index) => targetStatusNames.indexOf(statusName) !== index,
			);
		}, [availableTransitions]);

		const statusNames = useMemo(
			() =>
				shouldShowCreateStatusButton
					? selectOptions[0]?.options.map((option: StatusTransitionOption) => option.label)
					: [],
			[shouldShowCreateStatusButton, selectOptions],
		);

		const createStatusButtonRef = useRef<HTMLButtonElement>(null);
		const menuListRef = useRef<HTMLDivElement>(null);

		const selectComponents = useMemo(
			() => ({
				NoOptionsMessage: () =>
					inputValue ? (
						<>
							<LazySelectFetchError message={formatMessage(messages.noSearchResults)} />
							{isTMP &&
							isAdmin &&
							projectKey &&
							projectType &&
							issueTypeId &&
							expVal(
								'jira_inline_create_status_list_view',
								'isInlineCreateStatusEnabled',
								false,
							) ? (
								<CreateStatusButton
									statusNames={statusNames}
									projectId={String(projectId)}
									projectKey={projectKey}
									projectType={projectType}
									issueTypeId={issueTypeId}
									isNoResultsView
								/>
							) : (
								<></>
							)}
						</>
					) : (
						<LazySelectFetchError
							onTryAgain={handleMenuOpenChange}
							message={formatMessage(messages.cannotLoadTransitions)}
							analyticsCellName="status"
						/>
					),
				Group: GroupComponentWithStickyFooter,
				MenuList: expVal('jira_inline_edit_status_list_view', 'isInlineEditStatusEnabled', false)
					? ({ children, ...props }: MenuListComponentProps<SelectOption, false>) => (
							<components.MenuList {...props} innerRef={menuListRef}>
								{children}
							</components.MenuList>
						)
					: components.MenuList,
				Option: expVal('jira_inline_edit_status_list_view', 'isInlineEditStatusEnabled', false)
					? ({ children, ...optionProps }: OptionProps<SelectOption>) => (
							<OptionWithInlineConfigButton
								showEditButton={Boolean(isAdmin && isTMP && issueTypeId && projectKey)}
								editLabelText={formatMessage(editStatusMessages.editStatusTooltip)}
								projectId={String(projectId)}
								issueTypeId={issueTypeId ?? ''}
								projectKey={projectKey ?? ''}
								statusNames={transitions?.map((transition) => transition.to.name) ?? []}
								menuListRef={menuListRef}
								{...optionProps}
							>
								{children}
							</OptionWithInlineConfigButton>
						)
					: Option,
			}),
			[
				inputValue,
				formatMessage,
				isTMP,
				isAdmin,
				projectKey,
				projectType,
				issueTypeId,
				statusNames,
				projectId,
				handleMenuOpenChange,
				transitions,
			],
		);

		const getFormatOptionLabel = useCallback(
			(option: StatusTransitionOption | CreateStatusOption) => {
				if (
					isTMP &&
					isAdmin &&
					projectKey &&
					projectType &&
					issueTypeId &&
					option.optionType === CREATE_STATUS &&
					expVal('jira_inline_create_status_list_view', 'isInlineCreateStatusEnabled', false)
				) {
					return (
						<CreateStatusButton
							buttonRef={createStatusButtonRef}
							statusNames={statusNames}
							projectId={String(projectId)}
							projectKey={projectKey}
							projectType={projectType}
							issueTypeId={issueTypeId}
						/>
					);
				}
				const statusOption: StatusTransitionOption = option;
				if (statusOption.transition == null) {
					return statusOption.label || '';
				}
				return (
					<FormattedStatusOptionLabel
						option={statusOption}
						hasDuplicateStatusesInTargets={hasDuplicateStatusesInTargets}
					/>
				);
			},
			[
				hasDuplicateStatusesInTargets,
				isAdmin,
				isTMP,
				issueTypeId,
				projectId,
				projectKey,
				projectType,
				statusNames,
			],
		);

		const handleInputRef = useCallback(
			(ref: Ref<'input'>) => {
				if (inputRef) {
					// @ts-expect-error - TS2540 - Cannot assign to 'current' because it is a read-only property.
					// eslint-disable-next-line no-param-reassign
					inputRef.current = ref;
				}
			},
			[inputRef],
		);

		const popupContent = useCallback(
			() => (
				<CellContentWrapper
					// -1px border left from gridcell
					preferredWidth={width - 1}
					onKeyDown={handleContentKeyDown}
				>
					<LazySelect
						autoFocus
						menuIsOpen
						placeholder={formatMessage(messages.placeholder)}
						options={selectOptions}
						onChange={handleTransitionChange}
						value={selectValue}
						formatOptionLabel={getFormatOptionLabel}
						isMulti={false}
						isInvalid={false}
						isClearable={false}
						isLoading={serviceBusy}
						components={selectComponents}
						onInputRef={handleInputRef}
						tabSelectsValue={!!fg('jira_list_keyboard_nav')}
						inputValue={fg('jira_list_keyboard_nav') ? inputValue : undefined}
						onInputChange={fg('jira_list_keyboard_nav') ? handleInputChangeNew : handleInputChange}
					/>
				</CellContentWrapper>
			),
			[
				width,
				handleContentKeyDown,
				formatMessage,
				selectOptions,
				handleTransitionChange,
				selectValue,
				getFormatOptionLabel,
				serviceBusy,
				selectComponents,
				handleInputRef,
				inputValue,
				handleInputChangeNew,
				handleInputChange,
			],
		);

		const popupTrigger = useCallback(
			(triggerProps: TriggerProps) => (
				<Pressable {...triggerProps} xcss={popupTriggerStyles}>
					<CellContentContainer onClick={handleMenuOpenChange} width={width} height={height}>
						<Lozenge isBold={isLozengeBold} appearance={toAppearance(statusFromService)}>
							{statusFromService.name}
						</Lozenge>
					</CellContentContainer>
				</Pressable>
			),
			[handleMenuOpenChange, width, height, isLozengeBold, statusFromService],
		);

		const popupComponent = useMemo(
			() =>
				getPopupComponentWithHeight(
					shouldShowCreateStatusButton
						? {
								numOptions: selectOptions[0]?.options.length ?? 0,
								numActionOptions: selectOptions[1]?.options.length ?? 0,
								numGroups: Math.max(selectOptions.length - 1, 0),
							}
						: { numOptions: selectOptions.length },
				),
			[shouldShowCreateStatusButton, selectOptions],
		);

		const statusMenu = useMemo(
			() => (
				<Popup
					isOpen={isMenuOpen}
					placement="bottom-start"
					autoFocus={false}
					offset={POPUP_OFFSET}
					popupComponent={popupComponent}
					content={popupContent}
					trigger={popupTrigger}
					onClose={onPopupClose}
					testId="business-list.ui.list-view.status-cell.status-menu"
				/>
			),
			[isMenuOpen, onPopupClose, popupContent, popupTrigger, popupComponent],
		);

		return isHovering || isMenuOpen || isSelected ? (
			statusMenu
		) : (
			<Lozenge isBold={isLozengeBold} appearance={toAppearance(statusFromService)}>
				{statusFromService.name}
			</Lozenge>
		);
	},
);

const StatusCell = memo<StatusCellProps>(
	({
		id,
		name,
		categoryId,
		issueKey,
		width,
		height,
		onHighlightRow,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		rowData,
		editMode: editModeNew = false,
		setEditMode: setEditModeNew = noop,
		isHovering: isHoveringNew = false,
		inputValue,
		onInputChange,
	}: StatusCellProps) => {
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const containerRef = useRef<HTMLElement>(null);
		const inputRef = useRef<HTMLInputElement>(null);
		const [editMode, setEditMode] = fg('jira_list_keyboard_nav')
			? [editModeNew, setEditModeNew]
			: // eslint-disable-next-line react-hooks/rules-of-hooks
				useState<boolean>(false);
		const [isHoveringOld, setIsHovering] = useState(false);
		const isHovering = fg('jira_list_keyboard_nav') ? isHoveringNew : isHoveringOld;

		const [statusValue, setStatusValue] = useState<StatusValue>({
			id,
			name,
			statusCategory: { id: categoryId },
		});
		const { showFlag } = useFlagService();
		const [isRowSelected] = useIsIssueSelected({ issueId: String(rowData.issueId) });
		const isLozengeBold =
			isVisualRefreshEnabled() ||
			(isRowSelected && categoryId === StatusCategoryIds[StatusCategoryTypes.INDETERMINATE]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		useEffect(() => {
			// will close the popup and blur cell if user clicks on another cell
			if (!fg('jira_list_keyboard_nav') && !isSelected && editMode) {
				setEditMode(false);
			}
		}, [editMode, isSelected, setEditMode]);

		const onCompleted = useCallback(
			() => onHighlightRow && onHighlightRow(issueKey),
			[issueKey, onHighlightRow],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleKeyDown = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				e.stopPropagation();
				// clicking container in order to fetch transitions
				if (
					(e.key === 'Enter' || e.key === ' ') &&
					containerRef.current &&
					e.currentTarget === e.target
				) {
					containerRef.current.click();
				}

				!editMode && onKeyDown?.(e);
			},
			[editMode, containerRef, onKeyDown],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated wrapperRef is now managed in CellNavigation
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleWrapperClick = useCallback(() => {
			setEditMode(!editMode);
		}, [editMode, setEditMode]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleMouseEnter = useCallback(() => {
			setIsHovering(true);
		}, []);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleMouseLeave = useCallback(() => {
			setIsHovering(false);
		}, []);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleBlur = useCallback(
			(event: FocusEvent<HTMLElement>) => {
				if (!editMode) {
					onBlur?.(event);
				}
			},
			[onBlur, editMode],
		);

		const onPopupClose = useCallback(() => {
			setEditMode(false);
		}, [setEditMode]);

		const handleStatusChange = useCallback(
			(value: StatusValue) => {
				setStatusValue(value);

				if (!fg('jira_list_keyboard_nav')) {
					// Force the focus back on the cell
					// Without this, the focus is lost when the user selects a status
					setTimeout(() => {
						wrapperRef?.current?.focus();
					});
				}

				if (
					value.statusCategory.id === StatusCategoryIds[StatusCategoryTypes.DONE] &&
					!fg('show-modernised-issue-transition-success-flag')
				) {
					showFlag({
						id: 'marked-as-done-flag',
						isAutoDismiss: true,
						type: 'success',
						title: `🎉 ${formatMessage(messages.markedAsDoneFlagTitle)}`,
						description: [
							messages.markedAsDone,
							{
								issue: `${issueKey}`,
								statusName: value.name,
							},
						],
						actions: [
							{
								content: expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
									? messages.viewIssueIssueTermRefresh
									: messages.viewIssue,
								href: `/browse/${issueKey}`,
								target: '_blank',
							},
						],
					});

					onCompleted();
				}
			},
			[formatMessage, issueKey, onCompleted, showFlag, wrapperRef],
		);

		const handleCutCopy = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				event.preventDefault();

				if (statusValue) {
					event.clipboardData.setData('text/plain', statusValue.name);
				}

				if (event.type === 'copy') {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell copied', 'status');
				} else {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell cut', 'status');
				}
			},
			[statusValue, createAnalyticsEvent],
		);

		const handlePaste = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				const clipText = event.clipboardData.getData('text/plain');
				if (clipText) {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', 'status');
				}
			},
			[createAnalyticsEvent],
		);

		const transitionsChildren = useCallback(
			({
				transitions,
				statusFromService,
				transitionStatus,
				fetchTransitions,
				serviceBusy,
			}: TransitionsQueryChildrenProps) => (
				<Transitions
					transitions={transitions}
					statusFromService={statusFromService}
					transitionStatus={transitionStatus}
					fetchTransitions={fetchTransitions}
					serviceBusy={serviceBusy}
					isSelected={isSelected}
					isHovering={isHovering}
					isMenuOpen={editMode}
					isLozengeBold={isLozengeBold}
					width={width}
					height={height}
					wrapperRef={wrapperRef}
					containerRef={containerRef}
					inputRef={inputRef}
					onPopupClose={onPopupClose}
					onMenuOpen={setEditMode}
					inputValue={inputValue}
					onInputChange={onInputChange}
					issueTypeId={rowData.issuetype.id}
				/>
			),
			[
				isSelected,
				isHovering,
				editMode,
				isLozengeBold,
				width,
				height,
				wrapperRef,
				onPopupClose,
				setEditMode,
				inputValue,
				onInputChange,
				rowData.issuetype.id,
			],
		);

		const cellContent = (
			<ServiceWrapper
				id={id}
				issueId={String(rowData.issueId)}
				name={name}
				categoryId={categoryId}
				issueKey={issueKey}
				onStatusChange={handleStatusChange}
			>
				{transitionsChildren}
			</ServiceWrapper>
		);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<CellWrapperNew
					isEditable
					testId="business-list.ui.list-view.status-cell.cell-container"
					{...(!fg('jwm_list_new_copy_paste_behaviour')
						? {
								onCopy: handleCutCopy,
								onCut: handleCutCopy,
								onPaste: handlePaste,
							}
						: {})}
				>
					{cellContent}
				</CellWrapperNew>
			);
		}

		return (
			<CellWrapper
				isFocused={editMode || isSelected}
				isEditable
				onClick={handleWrapperClick}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				tabIndex={0}
				onFocus={onFocus}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				ref={onWrapperRef}
				onCopy={handleCutCopy}
				onCut={handleCutCopy}
				onPaste={handlePaste}
				data-testid="business-list.ui.list-view.status-cell.cell-container"
			>
				{cellContent}
			</CellWrapper>
		);
	},
);

export default StatusCell;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TransitionLabel = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		textOverflow: 'ellipsis',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TransitionName = styled.span({
	flex: 3,
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StatusName = styled.span({
	textAlign: 'right',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionWrapper = styled.span({
	display: 'flex',
	alignItems: 'center',
	borderRadius: token('border.radius', '3px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TransitionWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
});

const popupTriggerStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
});

const CreateStatusOptionStyle = css({
	padding: '0',
	boxShadow: 'none',
});
